import React from "react";
import { useForm } from "react-hook-form";
import { X } from "lucide-react";
import Input from "../../widgets/Input";
import Tooltip from "../../widgets/ToolTip";

interface Group {
  _id?: string;
  group_id: string;
  group_name: string;
  group_description: string;
  parent_group_id: string;
}

interface GroupFormProps {
  isOpen: boolean;
  onClose: () => void;
  group?: Group | null;
  onSubmit: (data: Group) => void;
  isLoading?: boolean;
}

interface GroupFormData {
  group_id: string;
  group_name: string;
  group_description: string;
  parent_group_id: string;
}

const GroupForm = ({
  isOpen,
  onClose,
  group,
  onSubmit,
  isLoading = false,
}: GroupFormProps) => {
  const { control, handleSubmit, reset } = useForm<GroupFormData>({
    mode: "onChange",
    defaultValues: {
      group_id: group?.group_id || "",
      group_name: group?.group_name || "",
      group_description: group?.group_description || "",
      parent_group_id: group?.parent_group_id || "root",
    },
  });

  React.useEffect(() => {
    if (group) {
      reset({
        group_id: group.group_id,
        group_name: group.group_name,
        group_description: group.group_description,
        parent_group_id: group.parent_group_id,
      });
    } else {
      reset({
        group_id: "",
        group_name: "",
        group_description: "",
        parent_group_id: "root",
      });
    }
  }, [group, isOpen, reset]);

  const handleFormSubmit = (data: GroupFormData) => {
    onSubmit({
      ...data,
      _id: group?._id,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {group ? "Edit Group" : "Add Group"}
          </h2>
          <Tooltip content="Close">
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            disabled={isLoading}
          >
            <X className="w-6 h-6" />
          </button>
          </Tooltip>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(handleFormSubmit)} className="p-6">
          <div className="space-y-4">
            <Input
              name="group_id"
              control={control}
              label="Group ID"
              placeholder="Enter group ID (e.g., GROUP001)"
              rules={{
                required: "Group ID is required",
                pattern: {
                  value: /^[A-Z0-9_]+$/,
                  message:
                    "Group ID must contain only uppercase letters, numbers, and underscores",
                },
              }}
              disabled={group as any}
            />

            <Input
              name="group_name"
              control={control}
              label="Group Name"
              placeholder="Enter group name (e.g., Administrator)"
              rules={{
                required: "Group name is required",
                minLength: {
                  value: 2,
                  message: "Group name must be at least 2 characters",
                },
                pattern: {
                  value: /^[A-Za-z0-9\s]*$/,
                  message: "Only alphabets, numbers, and spaces are allowed"
                }
              }}
              disabled={isLoading}
            />

            <Input
              name="group_description"
              control={control}
              label="Group Description"
              placeholder="Enter group description"
              rules={{
                required: "Group description is required",
                pattern: {
                  value: /^[A-Za-z0-9\s_.,-]*$/,
                  message: "Only alphabets, numbers, spaces, hyphens, underscores, commas and periods are allowed"
                }
              }}
              disabled={isLoading}
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              disabled={isLoading}
            >
              {isLoading
                ? "Saving..."
                : group
                ? "Update Group"
                : "Create Group"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GroupForm;
